import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';

export interface LabResult extends LabValue {
  document_id?: string;
  id: string;
  revision_id?: string;
  row_key: string;
  source_system?: string;
}
export interface LabValue {
  accession: string;
  collected_date: string | null;
  evidence: string;
  flag: string;
  loinc: string;
  method: string;
  page: number;
  reference_range: string;
  reported_date: string | null;
  specimen: string;
  status: string;
  test_name: string;
  unit: string;
  value: string;
}
interface Extraction {
  approved_indices?: number[];
  completed_pages?: number;
  error_code?: string;
  generation: number;
  id?: string;
  pages: { page: number; lines: string[]; confidence: number; processing_status?: string; processing_error?: string }[];
  results: LabValue[];
  status: string;
  total_pages?: number;
}
export function useLabAction(documentId: string, action: 'extract' | 'approve') {
  const { org, base } = useLabsApi();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async (body: unknown) => (await clientApi.post(`${base}/documents/${documentId}/${action}`, body)).data,
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['lab-extraction', org, documentId] });
      void cache.invalidateQueries({ queryKey: ['maternal-labs', org] });
    },
  });
}
export function useLabExtraction(
  documentId: string,
  enabled: boolean,
  primaryStatus?: string | null,
  revisionId?: string | null,
) {
  const { org, base } = useLabsApi();
  return useQuery({
    enabled: !!org && enabled,
    queryKey: ['lab-extraction', org, documentId, revisionId, primaryStatus],
    queryFn: async () => (await clientApi.get<Extraction>(`${base}/documents/${documentId}`)).data,
    refetchInterval: (q) =>
      ['extracting', 'queued'].includes(q.state.data?.status ?? '') ||
      (q.state.data?.status === 'not_started' && ['extracting', 'pending'].includes(primaryStatus ?? ''))
        ? 5000
        : false,
  });
}
export function useLabsApi() {
  const { organization } = useAuth();
  const org = organization?.organization_id;
  return { org, base: `/api/v1/organizations/${org}/labs` };
}
export function useLabSourceRevision(documentId?: string, revisionId?: string) {
  const { org } = useLabsApi();
  return useQuery({
    enabled: !!org && !!documentId && !!revisionId,
    queryKey: ['lab-source-revision', org, documentId, revisionId],
    queryFn: async () => {
      const { data } = await clientApi.get<
        { id: string; file_url: string; file_name: string; content_type: string; revision_number: number }[]
      >(`/api/v1/organizations/${org}/uploaded-documents/${documentId}/revisions`);
      const revision = data.find((r) => r.id === revisionId);
      if (!revision) throw new Error('Source revision unavailable');
      return revision;
    },
  });
}

export function useMaternalLabs(pregnancyId: string, enabled: boolean, patientId?: string) {
  const { org, base } = useLabsApi();
  return useQuery({
    enabled: !!org && enabled,
    queryKey: ['maternal-labs', org, patientId ? 'patient' : 'pregnancy', patientId || pregnancyId],
    queryFn: async () =>
      (
        await clientApi.get<{ results: LabResult[] }>(
          `${base}/${patientId ? 'patients' : 'pregnancies'}/${patientId || pregnancyId}`,
        )
      ).data,
  });
}
